// A date field in a form, in place of `<input type="date">`: with a mouse it is
// a button showing the day, which opens the calendar (date-popover.tsx); on a
// touch screen it stays the native field (the phone's own picker).
//
// Promise: the value is "YYYY-MM-DD" or "" (none); picking calls `onChange`
// with the day, the calendar's clear button (`clearLabel`) with "". The day
// shows in the screen's language; an empty field shows `placeholder`, faint.
import { getLocale } from "@trameria/i18n";
import { useRef, useState } from "react";
import { toLocalDate } from "./calendar-date";
import { DatePopover } from "./date-popover";
import { useCoarsePointer } from "./pointer";

export interface DateFieldProps {
  /** "YYYY-MM-DD", or "" for none. */
  value: string;
  onChange(value: string): void;
  /** Read aloud for the field and its panel. */
  label: string;
  id?: string;
  placeholder?: string;
  /** Shown in the panel while there is a value; clears it. */
  clearLabel?: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  /** IANA time zone for "today" in the calendar (the account's). */
  timeZone?: string;
  /** The field's look (the button and the native field alike). */
  className?: string;
}

export function DateField({
  value,
  onChange,
  label,
  id,
  placeholder,
  clearLabel,
  min,
  max,
  disabled,
  timeZone,
  className = "",
}: DateFieldProps) {
  const coarse = useCoarsePointer();
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  if (coarse)
    return (
      <input
        id={id}
        type="date"
        aria-label={label}
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        // Kept to the field's width and its text to the left (iOS kept it as
        // wide as its content and centered the day otherwise).
        className={`appearance-none [&::-webkit-date-and-time-value]:min-h-[1lh] [&::-webkit-date-and-time-value]:text-left ${className}`}
      />
    );
  const day = value ? toLocalDate(value) : null;
  return (
    <>
      <button
        ref={anchor}
        id={id}
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`cursor-pointer text-left tabular-nums disabled:cursor-default ${className}`}
      >
        {day ? (
          new Intl.DateTimeFormat(getLocale(), { dateStyle: "medium" }).format(
            day,
          )
        ) : (
          // A span, so its faint color does not fight the field's own.
          <span className="text-placeholder">{placeholder ?? " "}</span>
        )}
      </button>
      <DatePopover
        anchor={anchor}
        open={open}
        onClose={() => setOpen(false)}
        label={label}
        value={value}
        min={min}
        max={max}
        clearLabel={clearLabel}
        timeZone={timeZone}
        onChange={onChange}
      />
    </>
  );
}
